import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import ScreenContainer from 'common/components/screenContainer/ScreenContainer';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Box from 'common/components/box/Box';
import Button from 'common/components/button/Button';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import DashboardService from 'api/dashboard/DashboardService';
import { PlafondConsumptionItemDto } from 'api/dashboard/dtos/PlafondConsumptionDashboardDto';
import styles from './PlafondsReportScreen.module.scss';

/**
 * Task #66600 — relatório de consumo vs plafond da frota.
 *
 * Complementa o widget: mesma agregação, mas com a serie mensal completa e exportação para Excel,
 * que é o que um gestor de frota precisa para agir.
 */
const PlafondsReportScreen: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const [items, setItems] = useState<PlafondConsumptionItemDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [monthsBack, setMonthsBack] = useState<number>(6);
    const [onlyExceeded, setOnlyExceeded] = useState<boolean>(false);

    const criteria = useMemo(() => ({
        dateFrom: dayjs().subtract(monthsBack - 1, 'month').startOf('month').toDate(),
        dateTo: dayjs().endOf('month').toDate(),
    }), [monthsBack]);

    useEffect(() => {
        loadData();
    }, [monthsBack]);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const result = await DashboardService.getPlafondConsumption({ ...criteria });
            setItems(result?.items || []);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load the plafond consumption report', error);
            addToast(t('common.messages.error_load_info') as string, { appearance: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const onExport = async () => {
        try {
            Loading.show();
            const report = await DashboardService.getPlafondConsumptionReport({ ...criteria });
            const title = t('vehicle.plafonds.report_title' as any) as string;
            saveAs(report, `${title}_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.xlsx`);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t export the plafond consumption report', error);
            addToast(t('common.messages.error_load_info') as string, { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    };

    const rows = useMemo(() => {
        const sorted = [...items].sort((a, b) => {
            const byVehicle = (a.registrationNumber || '').localeCompare(b.registrationNumber || '');
            if (byVehicle !== 0) { return byVehicle; }
            const byName = (a.designation || '').localeCompare(b.designation || '');
            if (byName !== 0) { return byName; }
            return new Date(b.month).getTime() - new Date(a.month).getTime();
        });
        return onlyExceeded ? sorted.filter(x => x.exceeded) : sorted;
    }, [items, onlyExceeded]);

    return (
        <ScreenContainer>
            <ScreenTitle title={t('vehicle.plafonds.report_title' as any) as string} />

            <Box>
                <div className={styles.toolbar}>
                    <div className={styles.filters}>
                        {[3, 6, 12].map(months => (
                            <span
                                key={months}
                                className={`${styles.pill} ${monthsBack === months ? styles.pillActive : ''}`}
                                onClick={() => setMonthsBack(months)}
                            >
                                {months} {t('vehicle.plafonds.report_months' as any) as string}
                            </span>
                        ))}
                        <span
                            className={`${styles.pill} ${onlyExceeded ? styles.pillActive : ''}`}
                            onClick={() => setOnlyExceeded(!onlyExceeded)}
                        >
                            {t('vehicle.plafonds.exceeded' as any) as string}
                        </span>
                    </div>
                    <Button text={t('common.export') as string} onClick={onExport} isDisabled={rows.length === 0} />
                </div>

                {isLoading ? (
                    <div className={styles.centerAlign}><LoadingSpinner /></div>
                ) : rows.length === 0 ? (
                    <div className={styles.noInfoText}>{t('vehicle.plafonds.widget_no_plafonds' as any) as string}</div>
                ) : (
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('vehicle.plafonds.report_asset' as any) as string}</th>
                                    <th>{t('vehicle.plafonds.designation' as any) as string}</th>
                                    <th>{t('vehicle.plafonds.report_month' as any) as string}</th>
                                    <th className={styles.numeric}>{t('vehicle.plafonds.limit' as any) as string}</th>
                                    <th className={styles.numeric}>{t('vehicle.plafonds.consumed' as any) as string}</th>
                                    <th className={styles.numeric}>{t('vehicle.plafonds.overage' as any) as string}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(item => (
                                    <tr
                                        key={`${item.plafondId}_${new Date(item.month).getTime()}`}
                                        className={item.exceeded ? styles.rowExceeded : ''}
                                        // Click-through em nova aba, como nos widgets do painel.
                                        onClick={() => window.open(`/vehicles/details/${item.vehicleId}`, '_blank')}
                                    >
                                        <td>{item.registrationNumber}</td>
                                        <td>{item.designation}</td>
                                        <td className={styles.month}>{dayjs(item.month).format('MM/YYYY')}</td>
                                        <td className={styles.numeric}><MoneyFormat value={item.monthlyLimit} /></td>
                                        <td className={styles.numeric}><MoneyFormat value={item.consumed} /></td>
                                        <td className={styles.numeric}>
                                            {item.overage > 0 ? <MoneyFormat value={item.overage} /> : <>—</>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Box>
        </ScreenContainer>
    );
};

export default PlafondsReportScreen;
